import type { JSX } from "react";
import { Avatar, Icon, Stack, Text } from "../../components/ui";
import { formatTimeAgo } from "../../utils/time";
import type { ListItem } from "./types";

export interface BaseListItemProps {
  readonly item: ListItem;
  readonly themeColor?: string;
}

/**
 * Default list item renderer for BaseModule.
 * Shows: avatar + title + subtitle/preview + timestamp + pin icon.
 */
export function BaseListItem({
  item,
  themeColor = "orange",
}: BaseListItemProps): JSX.Element {
  const initials = item.name
    ? item.name
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "?";

  return (
    <>
      <Avatar
        label={initials}
        color={themeColor}
        size="md"
        imageSrc={item.avatarUrl ?? undefined}
      />
      <Stack gap={0.5} flex1>
        <Text variant="title" truncate className="list-item-title">
          {item.name ?? "Untitled"}
        </Text>
        {item.preview && (
          <Text variant="caption" truncate className="list-item-secondary">
            {item.preview}
          </Text>
        )}
      </Stack>
      <Stack gap={0.5} className="items-end shrink-0">
        {item.timestamp && (
          <Text variant="caption" className="text-content-tertiary whitespace-nowrap">
            {formatTimeAgo(item.timestamp)}
          </Text>
        )}
        {item.is_pinned && (
          <Icon name="pin" size={12} className="text-content-tertiary" />
        )}
      </Stack>
      {item.unread_count != null && item.unread_count > 0 && (
        <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[11px] font-semibold text-white">
          {item.unread_count}
        </span>
      )}
    </>
  );
}
